import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Loader2, KeyRound } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Brand } from "@/components/landing/SiteHeader";
import { useAuth } from "@/context/AuthContext";
import { api, formatErr } from "@/lib/api";

export default function StaffLogin() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [ch, setCh] = useState(null);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  if (user) return <Navigate to="/studio" replace />;

  const run = async (fn) => {
    setErr("");
    setLoading(true);
    try { await fn(); } catch (e) { setErr(formatErr(e)); } finally { setLoading(false); }
  };

  const login = (e) => { e.preventDefault(); run(async () => { const { data } = await api.post("/auth/login", { email, password }); setCh(data); }); };
  const verify = (e) => {
    e.preventDefault();
    run(async () => { const { data } = await api.post("/auth/verify-otp", { challenge_id: ch.challenge_id, code }); setUser(data.user); nav("/studio"); });
  };

  return (
    <main className="min-h-screen grid lg:grid-cols-2 bg-white" data-testid="staff-login-page">
      <div className="hidden lg:flex bg-navy grain relative flex-col justify-between p-12">
        <Brand light />
        <div className="relative">
          <p className="font-serif text-5xl text-white leading-tight">Gestionale pratiche</p>
          <p className="text-slate-400 mt-4 max-w-sm">Accesso riservato ai professionisti e collaboratori dello studio. Autenticazione a due fattori obbligatoria.</p>
        </div>
        <p className="text-xs text-slate-500 relative">Ogni accesso è registrato nel log di audit.</p>
      </div>
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-10"><Brand /></div>
          {!ch ? (
            <form onSubmit={login} className="space-y-5" data-testid="staff-login-form">
              <h1 className="font-serif text-4xl text-navy">Accedi</h1>
              <div><Label htmlFor="em">Email</Label><Input id="em" data-testid="staff-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-11" required /></div>
              <div><Label htmlFor="pw">Password</Label><Input id="pw" data-testid="staff-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 h-11" required /></div>
              {err && <p data-testid="staff-login-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</p>}
              <Button data-testid="staff-login-submit" type="submit" disabled={loading} className="w-full h-11 bg-navy hover:bg-navy-800">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Continua"}</Button>
            </form>
          ) : (
            <form onSubmit={verify} className="space-y-5" data-testid="staff-otp-form">
              <KeyRound className="h-8 w-8 text-gold-dark" strokeWidth={1.5} />
              <h1 className="font-serif text-4xl text-navy">Codice di verifica</h1>
              <p className="text-sm text-slate-600">Abbiamo inviato un codice a 6 cifre a <strong>{ch.email_masked}</strong>.</p>
              <InputOTP maxLength={6} value={code} onChange={setCode} data-testid="staff-otp-input">
                <InputOTPGroup>{[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="h-12 w-11 text-lg" />)}</InputOTPGroup>
              </InputOTP>
              {ch.dev_code && <p data-testid="staff-otp-dev-code" className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded px-3 py-2">Modalità sviluppo — codice: <span className="font-mono font-semibold">{ch.dev_code}</span></p>}
              {err && <p data-testid="staff-otp-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</p>}
              <Button data-testid="staff-otp-submit" type="submit" disabled={loading || code.length < 6} className="w-full h-11 bg-navy hover:bg-navy-800">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verifica e accedi"}</Button>
              <button type="button" data-testid="staff-otp-back" className="text-sm text-slate-500 underline" onClick={() => { setCh(null); setCode(""); }}>Torna indietro</button>
            </form>
          )}
        </div>
      </div>
    </main>
  );
}
